import { SimulationFormParams } from "../types/forms";
import { ModeKey, modes } from "../mode/modes";

const ACTIVE_HYDRATION_KEY = "mortgage-simulator-state";
const HISTORY_STORAGE_KEY = "mortgage-simulator-history";
const HISTORY_VERSION = 1;
const MAX_HISTORY_ENTRIES = 20;

export interface SessionHistoryEntry {
  id: string;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
  mode: ModeKey;
  simulations: SimulationFormParams[];
}

interface SessionHistoryStore {
  version: number;
  sessions: SessionHistoryEntry[];
}

/**
 * Validate that simulations is an array of objects each containing an `inputs` object.
 */
function validateSimulations(
  simulations: unknown,
): SimulationFormParams[] | null {
  if (!Array.isArray(simulations)) return null;

  for (const sim of simulations) {
    if (typeof sim !== "object" || sim === null) return null;
    if (
      !("inputs" in sim) ||
      typeof (sim as any).inputs !== "object" ||
      (sim as any).inputs === null
    ) {
      return null;
    }
  }

  return simulations as SimulationFormParams[];
}

// ─── Transient hydration state (for useSimulationFormModel on mount) ────────

interface HydrationState {
  mode: ModeKey;
  simulations: SimulationFormParams[];
}

/**
 * Write transient hydration data so useSimulationFormModel can pick it up on mount.
 */
function writeHydrationState(
  mode: ModeKey,
  simulations: SimulationFormParams[],
): void {
  try {
    const state: HydrationState = {
      mode,
      simulations: simulations.map((sim) => ({
        ...sim,
        updated: false,
        resultIndex: undefined,
      })),
    };
    localStorage.setItem(ACTIVE_HYDRATION_KEY, JSON.stringify(state));
  } catch (error) {
    console.error("Error writing hydration state:", error);
  }
}

/**
 * Read transient hydration data without clearing it.
 * Returns null if nothing is available.
 */
export function peekHydrationState(): HydrationState | null {
  try {
    const stored = localStorage.getItem(ACTIVE_HYDRATION_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    if (
      typeof parsed?.mode !== "string" ||
      !Object.prototype.hasOwnProperty.call(modes, parsed.mode)
    )
      return null;
    const sims = validateSimulations(parsed.simulations);
    if (!sims || sims.length === 0) return null;

    return { mode: parsed.mode as ModeKey, simulations: sims };
  } catch (error) {
    console.error("Error reading hydration state:", error);
    return null;
  }
}

/**
 * Clear any leftover transient state on app startup.
 * Also clears old-format mortgage-simulator-state for users migrating.
 */
export function clearTransientState(): void {
  try {
    localStorage.removeItem(ACTIVE_HYDRATION_KEY);
  } catch {
    // ignore
  }
}

// ─── Session History ────────────────────────────────────────────────────────

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
}

function cleanSimulations(
  simulations: SimulationFormParams[],
): SimulationFormParams[] {
  return simulations.map((sim) => ({
    ...sim,
    updated: false,
    resultIndex: undefined,
  }));
}

/**
 * Load session history from localStorage
 */
export function loadSessionHistory(): SessionHistoryEntry[] {
  try {
    const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!stored) return [];

    const parsed = JSON.parse(stored) as SessionHistoryStore;

    if (parsed.version !== HISTORY_VERSION) {
      console.warn("Session history version mismatch. Clearing history.");
      clearSessionHistory();
      return [];
    }

    if (!Array.isArray(parsed.sessions)) return [];

    // Validate each session entry and filter out corrupted ones
    const valid = parsed.sessions.filter((session) => {
      const s = session as any;
      if (typeof s !== "object" || s === null) return false;
      if (typeof s.id !== "string") return false;
      if (typeof s.createdAt !== "string") return false;
      if (typeof s.updatedAt !== "string") return false;
      if (
        typeof s.mode !== "string" ||
        !Object.prototype.hasOwnProperty.call(modes, s.mode)
      )
        return false;
      const validSims = validateSimulations(s.simulations);
      return validSims !== null && validSims.length > 0;
    });

    // Persist the cleaned list if any corrupted entries were removed
    if (valid.length !== parsed.sessions.length) {
      saveHistoryStore(valid);
    }

    return valid;
  } catch (error) {
    console.error("Error loading session history:", error);
    return [];
  }
}

function saveHistoryStore(sessions: SessionHistoryEntry[]): void {
  try {
    const store: SessionHistoryStore = {
      version: HISTORY_VERSION,
      sessions,
    };
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    console.error("Error saving session history:", error);
  }
}

/**
 * Create a new session in history. Returns the new session's id.
 */
export function createSession(
  mode: ModeKey,
  simulations: SimulationFormParams[],
): string {
  const sessions = loadSessionHistory();
  const now = new Date().toISOString();
  const entry: SessionHistoryEntry = {
    id: generateId(),
    createdAt: now,
    updatedAt: now,
    mode,
    simulations: cleanSimulations(simulations),
  };

  sessions.unshift(entry);

  // Evict oldest entries if over the limit
  if (sessions.length > MAX_HISTORY_ENTRIES) {
    sessions.length = MAX_HISTORY_ENTRIES;
  }

  saveHistoryStore(sessions);
  return entry.id;
}

/**
 * Update an existing session in history.
 * Returns true if the session was found and updated, false if not found.
 */
export function updateSession(
  id: string,
  mode: ModeKey,
  simulations: SimulationFormParams[],
): boolean {
  const sessions = loadSessionHistory();
  const idx = sessions.findIndex((s) => s.id === id);
  if (idx === -1) return false;

  const cleaned = cleanSimulations(simulations);

  sessions[idx] = {
    ...sessions[idx],
    updatedAt: new Date().toISOString(),
    mode,
    simulations: cleaned,
  };

  // Move updated session to the front
  const [updated] = sessions.splice(idx, 1);
  sessions.unshift(updated);

  saveHistoryStore(sessions);
  return true;
}

/**
 * Save a session: creates if no id, updates if id exists.
 * Falls back to creating a new session if the id is no longer found in history.
 * Returns the session id.
 */
export function saveSession(
  id: string | null,
  mode: ModeKey,
  simulations: SimulationFormParams[],
): string {
  if (id && updateSession(id, mode, simulations)) {
    return id;
  }
  return createSession(mode, simulations);
}

/**
 * Prepare hydration state for resuming a session from the carousel.
 * Writes the session data to transient storage so useSimulationFormModel
 * picks it up after a remount.
 */
export function prepareSessionForResume(session: SessionHistoryEntry): void {
  writeHydrationState(session.mode, session.simulations);
}

/**
 * Delete a single session from history
 */
export function deleteSessionFromHistory(id: string): void {
  const sessions = loadSessionHistory();
  saveHistoryStore(sessions.filter((s) => s.id !== id));
}

/**
 * Clear all session history
 */
export function clearSessionHistory(): void {
  try {
    localStorage.removeItem(HISTORY_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_HYDRATION_KEY);
    localStorage.removeItem("globalSettings");
  } catch (error) {
    console.error("Error clearing session history:", error);
  }
}
